import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDateTime, daysWaiting } from "@/lib/insightsApi";
import {
  GitBranch, CheckCircle2, Circle, RotateCcw, XCircle, Send, ShieldAlert, Clock, UserCog,
} from "lucide-react";

const ACTION_LABELS = {
  workflow_started: "started the workflow", entered_step: "→ entered",
  approved: "approved", returned: "returned for revision", resubmitted: "resubmitted",
  denied: "denied", finalized: "finalized", reassigned: "reassigned the step",
};
const STATE_ICON = { done: CheckCircle2, current: GitBranch, pending: Circle };

export default function AdvancedWorkflowPanel({ insightId, user, insight, reload }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // { action } | { reassign }
  const [note, setNote] = useState("");
  const [newUser, setNewUser] = useState("");
  const [users, setUsers] = useState([]);
  const isAdmin = user?.role === "administrator";
  const isCreator = insight.created_by === user?.user_id;

  const load = useCallback(async () => {
    try { const r = await api.get(`/insights/${insightId}/workflow`); setState(r.data); }
    catch { setState(false); }
  }, [insightId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {}); }, [isAdmin]);

  const refresh = async () => { await load(); if (reload) await reload(); };

  const submit = async () => {
    setBusy(true);
    try { await api.post(`/insights/${insightId}/workflow/submit`); toast.success("Submitted"); await refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doAction = async (action, requireNote) => {
    if (requireNote && !note.trim()) { toast.error("A note is required."); return; }
    setBusy(true);
    try {
      await api.post(`/insights/${insightId}/workflow/action`, { action, note: note.trim() });
      toast.success("Workflow updated"); setDialog(null); setNote(""); await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doReassign = async () => {
    if (!newUser || !note.trim()) { toast.error("Select a user and provide a reason."); return; }
    setBusy(true);
    try {
      await api.post(`/insights/${insightId}/workflow/reassign`, { new_user_id: newUser, reason: note.trim() });
      toast.success("Step reassigned"); setDialog(null); setNote(""); setNewUser(""); await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!state) return <Card data-testid="workflow-panel"><p className="text-sm text-slate-400">Loading workflow…</p></Card>;
  if (state === false || state.engine !== "advanced") return null;

  const cur = state.current_step;
  const atCreator = cur?.step_type === "creator";
  const active = state.status === "active";
  const canAct = state.can_act && active && cur && !atCreator;
  const age = daysWaiting(state.waiting_since);

  return (
    <Card data-testid="workflow-panel">
      <div className="flex items-center gap-2 mb-3"><GitBranch className="h-4 w-4 text-brand-civic" /><h2 className="font-display font-bold text-brand-ink text-base">Workflow</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-slate-400">v{state.version}</span></div>

      {state.status === "finalized" && (
        <div data-testid="wf-finalized" className="mb-3 rounded-lg border border-brand-teal/40 bg-brand-teal-50 p-2.5 text-sm text-brand-teal">Finalized — staff workflow complete.</div>
      )}
      {state.status === "denied" && (
        <div data-testid="wf-denied" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-600">Denied — workflow ended.</div>
      )}
      {state.routing_issue && (
        <div data-testid="wf-routing-issue" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-800 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Routing needs attention — no eligible approver for the current step. An Administrator can reassign it.</span>
        </div>
      )}

      {/* WAITING FOR / NEXT */}
      {active && cur && !atCreator && (
        <div className="mb-3 rounded-lg border border-brand-line bg-brand-bg p-3" data-testid="wf-waiting-for">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Waiting For</div>
          <div className="text-sm font-semibold text-brand-ink mt-0.5">{cur.name} · {cur.assignment}</div>
          {(state.waiting_for || []).length > 0 && (
            <div className="text-xs text-slate-500 mt-0.5">{(state.waiting_for || []).map((w) => w.name).join(", ")}</div>
          )}
          {age != null && <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {age === 0 ? "Today" : `Waiting ${age}d`}</div>}
          {state.next_step && <div className="text-xs text-slate-500 mt-2">Next: <span className="font-medium text-brand-ink">{state.next_step.name}</span> · {state.next_step.assignment}</div>}
        </div>
      )}
      {active && atCreator && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="wf-with-creator">Returned to the Creator for revision. Edit the content, then resubmit.</div>
      )}

      {/* PROGRESS */}
      <ol className="space-y-2 mb-4" data-testid="wf-progress">
        {(state.progress || []).map((n) => {
          const Icon = STATE_ICON[n.state];
          return (
            <li key={n.step_id} className="flex items-center gap-2 text-sm" data-testid={`wf-progress-${n.state}`}>
              <span className={`h-5 w-5 rounded-full flex items-center justify-center ${n.state === "done" ? "bg-brand-teal text-white" : n.state === "current" ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-400"}`}>
                <Icon className="h-3 w-3" />
              </span>
              <span className={n.state === "current" ? "font-semibold text-brand-ink" : "text-slate-500"}>{n.name}</span>
              <span className="ml-auto text-[10px] text-slate-400">{n.assignment}</span>
            </li>
          );
        })}
      </ol>

      {/* ACTIONS */}
      <div className="space-y-2">
        {!state.started && (
          <Btn testid="wf-adv-submit" icon={Send} label="Submit for Review" onClick={submit} disabled={busy} />
        )}
        {active && atCreator && isCreator && (
          <Btn testid="wf-adv-resubmit" icon={Send} label="Resubmit" onClick={submit} disabled={busy} />
        )}
        {canAct && (
          <>
            <Btn testid="wf-adv-approve" icon={CheckCircle2} label="Approve" primary onClick={() => doAction("approve", false)} disabled={busy} />
            <Btn testid="wf-adv-return" icon={RotateCcw} label="Return for Revision" onClick={() => setDialog({ action: "return" })} disabled={busy} />
            <Btn testid="wf-adv-deny" icon={XCircle} label="Deny" danger onClick={() => setDialog({ action: "deny" })} disabled={busy} />
          </>
        )}
        {isAdmin && active && cur && !atCreator && (
          <div className="pt-2 mt-2 border-t border-dashed border-brand-line">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><UserCog className="h-3 w-3" /> Administrative Action</p>
            <Btn testid="wf-adv-reassign" icon={UserCog} label="Reassign Current Step" onClick={() => setDialog({ reassign: true })} disabled={busy} />
          </div>
        )}
        {!state.started === false && !canAct && !atCreator && active && (
          <p className="text-xs text-slate-400">You are not an approver for the current step.</p>
        )}
      </div>

      {/* HISTORY */}
      <div className="mt-4 pt-4 border-t border-brand-line">
        <h3 className="font-display font-semibold text-brand-ink text-sm mb-2">Approval History</h3>
        {(!state.history || state.history.length === 0) ? <p className="text-sm text-slate-400">No actions yet.</p> : (
          <ol className="space-y-2" data-testid="wf-history">
            {state.history.filter((h) => h.action !== "entered_step").map((h, i) => (
              <li key={i} className="flex gap-2 text-sm" data-testid="wf-history-item">
                <span className="mt-1 h-2 w-2 rounded-full bg-brand-civic shrink-0" />
                <div>
                  <p className="text-brand-ink"><strong>{h.actor_name || "Someone"}</strong> {ACTION_LABELS[h.action] || h.action} <span className="text-slate-500">· {h.step_name}</span></p>
                  {h.note && <p className="text-xs text-slate-500 italic">“{h.note}”</p>}
                  <p className="text-xs text-slate-400">{fmtDateTime(h.timestamp)}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) { setDialog(null); setNote(""); setNewUser(""); } }}>
        <DialogContent data-testid="wf-adv-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.action === "return" && "Return for Revision"}
              {dialog?.action === "deny" && "Deny Insight"}
              {dialog?.reassign && "Reassign Current Step"}
            </DialogTitle>
            <DialogDescription>
              {dialog?.action === "return" && "This returns the Insight along the configured Return path. A note is required."}
              {dialog?.action === "deny" && "Denial ends the workflow. A note is required."}
              {dialog?.reassign && "Assign the current step to a specific active staff user. The workflow template is not changed. A reason is required and audited."}
            </DialogDescription>
          </DialogHeader>
          {dialog?.reassign && (
            <select data-testid="wf-reassign-user" value={newUser} onChange={(e) => setNewUser(e.target.value)} className="h-10 w-full rounded-lg border border-brand-line px-2 text-sm">
              <option value="">Select a staff user…</option>
              {users.filter((u) => u.role !== "viewer").map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email}</option>)}
            </select>
          )}
          <textarea data-testid="wf-adv-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Reason / note…" className="w-full rounded-lg border border-brand-line p-3 text-sm outline-none focus:border-brand-civic" />
          <DialogFooter>
            <button onClick={() => { setDialog(null); setNote(""); setNewUser(""); }} className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg">Cancel</button>
            <button data-testid="wf-adv-confirm" disabled={busy}
              onClick={() => { if (dialog?.reassign) doReassign(); else doAction(dialog.action, true); }}
              className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Btn({ testid, icon: Icon, label, onClick, disabled, primary, danger }) {
  const cls = primary ? "bg-brand-teal text-white hover:bg-brand-teal/90 border-brand-teal"
    : danger ? "bg-white text-red-600 border-red-200 hover:bg-red-50"
      : "bg-brand-navy text-white hover:bg-brand-navy-900 border-brand-navy";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40 ${cls}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
